import { BookDashed, Bot, Code, Globe, Lock, Users } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

/**
 * One quiet line saying who can read something ("Restricted · 6 people"), plus how many sources
 * the viewer cannot see when there are some: their number only, never what they are. When the
 * content used web results, the line says so ("Includes web results"); an answer citing no
 * source says it is a general answer.
 */
export function AudienceLine(props: {
  text: string;
  restricted: boolean;
  /** Already-worded count of hidden sources ("2 sources you can't see"), if any. */
  hidden?: string | null;
  /** The content draws on public web results. */
  webResults?: boolean;
  /** Code ran for the content (agent-turn D20): "Ran code". */
  ranCode?: boolean;
  /** An answer that cites no source: "General answer, no sources used". */
  general?: boolean;
  /** The content draws on documents the agent wrote (artefacts-to-knowledge). */
  generatedSources?: boolean;
  className?: string;
  children?: ReactNode;
}) {
  const Icon = props.restricted ? Lock : Users;
  return (
    <p
      className={cn(
        "flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-fg-muted",
        props.className,
      )}
    >
      <Icon aria-hidden="true" className="size-3.5 shrink-0" strokeWidth={1.75} />
      <span className="min-w-0">{props.text}</span>
      {props.hidden ? (
        <>
          <span aria-hidden="true">·</span>
          <span>{props.hidden}</span>
        </>
      ) : null}
      {props.webResults ? (
        <>
          <span aria-hidden="true">·</span>
          <WebResultsMark />
        </>
      ) : null}
      {props.ranCode ? (
        <>
          <span aria-hidden="true">·</span>
          <RanCodeMark />
        </>
      ) : null}
      {props.general ? (
        <>
          <span aria-hidden="true">·</span>
          <GeneralAnswerMark />
        </>
      ) : null}
      {props.generatedSources ? (
        <>
          <span aria-hidden="true">·</span>
          <GeneratedSourcesMark />
        </>
      ) : null}
      {props.children}
    </p>
  );
}

function WebResultsMark() {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1">
      <Globe aria-hidden="true" className="size-3.5 shrink-0" strokeWidth={1.75} />
      {t("audience.webResults")}
    </span>
  );
}

/** "Includes web results" on its own line, where no audience line is shown. */
export function WebResultsNote({
  className,
  children,
}: {
  className?: string;
  /** Further marks on the same line (the answer's model). */
  children?: ReactNode;
}) {
  return (
    <p className={cn("flex flex-wrap items-center gap-x-1.5 text-xs text-fg-muted", className)}>
      <WebResultsMark />
      {children}
    </p>
  );
}

function RanCodeMark() {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1">
      <Code aria-hidden="true" className="size-3.5 shrink-0" strokeWidth={1.75} />
      {t("audience.ranCode")}
    </span>
  );
}

function GeneralAnswerMark() {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1">
      <BookDashed aria-hidden="true" className="size-3.5 shrink-0" strokeWidth={1.75} />
      {t("audience.generalAnswer")}
    </span>
  );
}

/** "General answer, no sources used" on its own line, where no audience line is shown. */
export function GeneralAnswerNote({ children }: { children?: ReactNode }) {
  return (
    <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-fg-muted">
      <GeneralAnswerMark />
      {children}
    </p>
  );
}

function GeneratedSourcesMark() {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1">
      <Bot aria-hidden="true" className="size-3.5 shrink-0" strokeWidth={1.75} />
      {t("generated.usesGenerated")}
    </span>
  );
}
